import api from './api.service';

export type ApprovalBriefEvidence = {
  source: string;
  ref?: string;
  detail: string;
};

export type ApprovalBriefClaim = {
  text: string;
  evidence: ApprovalBriefEvidence[];
};

export type ApprovalBriefMetric = {
  label: string;
  value: string;
  evidence?: ApprovalBriefEvidence[];
};

export type ApprovalBriefAnalysisStatus = 'FULL' | 'PARTIAL' | 'STUB' | 'UNAVAILABLE' | 'FAILED';

export type ApprovalBriefRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'UNKNOWN';

export type ApprovalBriefRisk = {
  category: string;
  level: ApprovalBriefRiskLevel;
  summary: string;
  evidence: ApprovalBriefEvidence[];
};

export type ApprovalBriefPolicyCheck = {
  label: string;
  status: 'pass' | 'warn' | 'fail' | 'unknown';
  detail: string;
  evidence: ApprovalBriefEvidence[];
};

export type ProjectHealthLevel = 'Healthy' | 'Attention needed' | 'Financial risk' | 'Cancelled';

export type ApprovalBriefProjectHealthEntry = {
  projectId: string;
  projectNumber: string | null;
  projectName: string | null;
  health: ProjectHealthLevel;
  blockers: ApprovalBriefClaim[];
  metrics: ApprovalBriefMetric[];
};

export type ApprovalBriefProjectHealth = {
  projects: ApprovalBriefProjectHealthEntry[];
  multiProject: boolean;
  gaps: ApprovalBriefClaim[];
};

export type DecisionIntelligence = {
  impacts: {
    onApprove: ApprovalBriefClaim[];
    onReject: ApprovalBriefClaim[];
  };
  historical: {
    similarCount: number | null;
    avgValue: number | null;
    avgDaysToApprove: number | null;
    commentary: ApprovalBriefClaim[];
    gaps: ApprovalBriefClaim[];
  };
  patterns: { items: ApprovalBriefClaim[]; gaps: ApprovalBriefClaim[] };
  knowledge: { items: ApprovalBriefClaim[]; gaps: ApprovalBriefClaim[] };
  /** Phase 5 — validated organizational memory (additive). */
  enterpriseMemory?: {
    items: ApprovalBriefClaim[];
    contradictions: Array<{
      severity: ApprovalBriefRiskLevel;
      summary: string;
      left: string;
      right: string;
      evidence: ApprovalBriefEvidence[];
    }>;
    gaps: ApprovalBriefClaim[];
  };
  alternatives: Array<{
    kind: 'supplier' | 'stock' | 'other';
    summary: string;
    evidence: ApprovalBriefEvidence[];
  }>;
  contradictions: Array<{
    severity: ApprovalBriefRiskLevel;
    summary: string;
    left: string;
    right: string;
    evidence: ApprovalBriefEvidence[];
  }>;
  reasoningChains: Array<{
    id: string;
    category: string;
    evidenceSummary: string;
    ruleId: string;
    level?: ApprovalBriefRiskLevel;
  }>;
  enrichedAt: string;
  analyzerErrors: string[];
};

export type ExecutiveDecisionContext = {
  entityType: string;
  entityId: string;
  lens: 'ceo' | 'finance' | 'operations' | 'approver';
  headline: ApprovalBriefClaim;
  highlightedMetrics: ApprovalBriefMetric[];
  focusAreas: ApprovalBriefClaim[];
  generatedAt: string;
};

export type ApprovalBrief = {
  entityType: string;
  entityId: string;
  documentNumber: string | null;
  documentStatus: string | null;
  coverage: 'full' | 'stub';
  analysisStatus?: ApprovalBriefAnalysisStatus;
  dataAsOf?: string | null;
  stale?: boolean;
  generatedAt: string;
  contentHash: string;
  narrativeGenerated: boolean;
  executiveSummary: ApprovalBriefClaim | null;
  businessContext: ApprovalBriefClaim[];
  financialImpact: {
    metrics: ApprovalBriefMetric[];
    commentary: ApprovalBriefClaim | null;
  };
  supplierIntelligence: {
    metrics: ApprovalBriefMetric[];
    commentary: ApprovalBriefClaim | null;
  } | null;
  commercialIntelligence: ApprovalBriefClaim[];
  historicalComparison: ApprovalBriefClaim[];
  riskAssessment: ApprovalBriefRisk[];
  policyCompliance: ApprovalBriefPolicyCheck[];
  crossModuleImpact: {
    onApprove: ApprovalBriefClaim[];
    onReject: ApprovalBriefClaim[];
  };
  questionsToAsk: ApprovalBriefClaim[];
  recommendation: ApprovalBriefClaim | null;
  confidenceScore: {
    percent: number;
    factors: ApprovalBriefClaim[];
  };
  itemsRequiringAttention: ApprovalBriefClaim[];
  gaps: ApprovalBriefClaim[];
  projectHealth?: ApprovalBriefProjectHealth;
  decisionIntelligence?: DecisionIntelligence;
  governance?: {
    versions: {
      evidenceVersion: string;
      analyzerVersion: string;
      narrativeVersion: string | null;
      promptVersion: string | null;
      promptId: string | null;
      modelVersion: string | null;
      modelId: string | null;
      policyVersion: string;
      schemaVersion: string;
    };
    trustScore: number;
    trustBand: string;
    predictionIds: string[];
    capturedAt: string;
  };
};

export const aiApprovalBriefService = {
  async getBrief(entityType: string, entityId: string, refresh = false): Promise<ApprovalBrief> {
    const q = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
    });
    if (refresh) q.set('refresh', '1');
    // api.get already returns the JSON body ({ success, data }), not AxiosResponse
    const body = (await api.get(`/ai-agent/approval-brief?${q.toString()}`)) as {
      success?: boolean;
      data?: ApprovalBrief;
      message?: string;
    };
    if (!body?.data) {
      throw new Error(body?.message || 'Approval brief unavailable');
    }
    return body.data;
  },

  async getExecutiveContext(entityType: string, entityId: string): Promise<ExecutiveDecisionContext> {
    const q = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
    });
    const body = (await api.get(`/ai-agent/approval-brief/executive?${q.toString()}`)) as {
      success?: boolean;
      data?: ExecutiveDecisionContext;
      message?: string;
    };
    if (!body?.data) {
      throw new Error(body?.message || 'Executive context unavailable');
    }
    return body.data;
  },
};
